import {useEffect} from "react";

import {useRouter} from "next/navigation";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - nprogress is a dependency of nextjs-toploader
import nProgress from "nprogress";

import type {IServerNavigable} from "@/shared/contracts";

/**
 * Hook for synchronizing ServerRouter with Next.js router
 * Subscribes to events and performs full navigation through router.push/replace
 *
 * @param model - Model that implements IServerNavigable
 *
 * @example
 * ```tsx
 * function MyView({ model }) {
 *   useServerRouter(model);
 *   // ...
 * }
 * ```
 */
export function useServerRouter(model: IServerNavigable): void {
    const router = useRouter();

    useEffect(() => {
        const unsubNavigate = model.serverRouter.events.on("navigate", (path, options) => {
            const {showProgressBar = true, ...nextOptions} = options || {};
            if (showProgressBar) {
                nProgress.start();
            }
            router.push(path, nextOptions);
        });

        const unsubReplace = model.serverRouter.events.on("replace", (path, options) => {
            const {showProgressBar = true, ...nextOptions} = options || {};
            if (showProgressBar) {
                nProgress.start();
            }
            router.replace(path, nextOptions);
        });

        const unsubBack = model.serverRouter.events.on("back", () => {
            router.back();
        });

        const unsubForward = model.serverRouter.events.on("forward", () => {
            router.forward();
        });

        const unsubRefresh = model.serverRouter.events.on("refresh", () => {
            router.refresh();
        });

        return () => {
            unsubNavigate();
            unsubReplace();
            unsubBack();
            unsubForward();
            unsubRefresh();
        };
    }, [router, model]);
}
